import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import moment from 'moment';
import sanitizeHtml from 'sanitize-html';

import { Search } from '../../ui/search';
import MocksButton from '../../misc/mocks-button';
import { Table, Column } from '../../ui/table';
import Pagination from '../../base/common/pagination';
import BasicDropdown from '../../base/dropdowns/basic-dropdown';
import ConfirmDropdown from '../rates/confirm-dropdown';
import { linksService } from './links-service';
import { session } from '../../../services/session';
import LinkOptions from './link-options';
import ClipboardArea from './clipboard-area';
import Icon from '../../ui/icon';
import SnackBar from '../../admin/accounts/snackbar';

import './manage-links.scss';

const PER_PAGE = 20;

const channelOptions = [
  { label: 'Youtube', value: 'youtube', selected: true },
  { label: 'Instagram', value: 'instagram', selected: true },
  { label: 'Tiktok', value: 'tiktok', selected: true },
  { label: 'Facebook', value: 'facebook', selected: true },
  { label: 'Site', value: 'site', selected: true },
  { label: 'Other', value: 'other', selected: true },
];

const ManageLinks = ({ flags, match }) => {
  const { publisherId, currentPage } = match.params;
  const sourceOptions = [
    { label: 'All Links', value: 'all' },
    { label: 'Created by You', value: 'user' },
    { label: 'Created By Team', value: 'team' },
    { label: 'Created By Narrativ', value: 'admin' },
  ];

  const [links, setLinks] = useState([]);
  const [showMocks, setShowMocks] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [source, setsource] = useState(sourceOptions[0]);
  const [channels, setChannels] = useState(channelOptions);
  const [page, setPage] = useState(currentPage || 1);
  const [perPage, setPerPage] = useState(PER_PAGE);
  const [totalItems, setTotalItems] = useState(0);
  const [snackbarTxt, setSnackbarTxt] = useState('');
  const [sortField, setSortField] = useState('datetime_link_created');
  const [sortDirection, setSortDirection] = useState('desc');

  const columns = [
    {
      dataKey: 'options',
      label: '',
      width: 0.2,
      cellRender: ({ rowData }) => {
        const productName = rowData.product_name
          ? sanitizeHtml(rowData.product_name, {
              allowedTags: [],
            })
          : rowData.bam_link_url;
        return (
          <LinkOptions
            copyLink={setCopyLink(
              rowData.datetime_link_created,
              rowData.encoded_auction_url,
              rowData.auction_url,
            )}
            productName={productName}
            getStory={() => linksService.getEdit(rowData.pub_id, { edit_id: rowData.edit_id })}
            editStory={params => handleEditStory(rowData.pub_id, params)}
            setSnackbarTxt={setSnackbarTxt}
          />
        );
      },
    },
    {
      dataKey: 'edit_name',
      label: 'Post',
      width: 0.9,
      justifyContent: 'left',
      cellRender: ({ cellData }) => (
        <div className="edit-name" dangerouslySetInnerHTML={santizeAndSetDangerousHtml(cellData)} />
      ),
    },
    {
      dataKey: 'product_name',
      label: 'Product',
      width: 0.8,
      justifyContent: 'left',
      cellRender: ({ rowData }) => {
        // TODO use when auction status is finalized
        // const productNameDisplay = ['Loading Status...', 'Processing'].includes(
        //   rowData.monetizeable_status,
        // )
        //   ? rowData.product_url
        //   : rowData.product_name;
        const productName = rowData.product_name || rowData.bam_link_url;
        const productNameDisplayFormatted = truncateLink(productName);
        const sanitizedProductName = santizeAndSetDangerousHtml(productNameDisplayFormatted);

        return (
          <div className="product-details">
            <div className="product-display-name" dangerouslySetInnerHTML={sanitizedProductName} />
          </div>
        );
      },
    },
    {
      dataKey: 'linkout',
      label: '',
      justifyContent: 'left',
      width: 0.3,
      cellRender: ({ rowData }) => (
        <a href={rowData.bam_link_url} target="_blank" rel="noopener noreferrer">
          <Icon className="linkout" iconName="linkOut" size={13} />
        </a>
      ),
    },
    {
      dataKey: 'edit_channel',
      label: 'Channel',
      width: 0.3,
      justifyContent: 'left',
    },
    {
      dataKey: 'datetime_link_created',
      label: 'Created On',
      width: 0.4,
      justifyContent: 'left',
      sortable: true,
      cellRender: ({ cellData }) => (
        <div>{moment(cellData, 'YYYY-MM-DD').format('MM/DD/YYYY')}</div>
      ),
    },
    {
      dataKey: 'auction_url',
      label: 'Narrativ Link',
      width: 1.2,
      justifyContent: 'left',
      cellRender: ({ rowData }) => {
        // TODO use when monetization status is finalized
        // const monetizeableStatus = rowData.monetizeable_status || 'Loading Status...';
        // const statusColor = ['Loading Status...', 'Processing'].includes(
        //   rowData.monetizeable_status,
        // )
        //   ? 'loading'
        //   : rowData.monetizeable_status === 'Monetizeable'
        //   ? 'monetizeable'
        //   : 'non-monetizeable';
        const auctionUrl = setCopyLink(
          rowData.datetime_link_created,
          rowData.encoded_auction_url,
          rowData.auction_url,
        );

        const bamLinkTruncated = truncateLink(auctionUrl);
        return <ClipboardArea bamLink={rowData.auction_url} bamLinkTruncated={bamLinkTruncated} />;
      },
    },
  ];

  useEffect(() => {
    getLinks();
  }, [channels, source, page, perPage, sortField, sortDirection]);

  useEffect(() => {
    setPage(1);
    getLinks();
  }, [searchQuery]);

  useEffect(() => {
    if (showMocks) setLinks(linksService.getManageLinksMockData());
  }, [showMocks]);

  async function getLinks() {
    const selectedChannels = channels.reduce(
      (channelArr, channel) => (channel.selected ? [...channelArr, channel.value] : channelArr),
      [],
    );

    const params = {
      page,
      per_page: perPage,
      source: source.value,
      is_fuzzy: flags.fuzzyLinkSearch,
      order_by: sortField,
      direction: sortDirection,
      ...(selectedChannels.length === channelOptions.length || !selectedChannels.length
        ? {}
        : { channels: selectedChannels.join(',') }),
      ...(searchQuery ? { search_query: searchQuery } : {}),
    };

    const res = await linksService.getLinks(publisherId, params);
    setLinks(res.data.data[0].links);
    setTotalItems(res.data.data[0].total_items);
  }

  async function handleEditStory(pubId, params) {
    const updatedStoryResp = await linksService.editStory(pubId, params);
    const updatedStory = updatedStoryResp.data.data[0];
    const updatedLinks = links.map(link =>
      link.edit_id === updatedStory.edit_id
        ? { ...link, edit_name: updatedStory.name, edit_channel: updatedStory.channel }
        : link,
    );
    setLinks(updatedLinks);
    return updatedStory;
  }

  // raw HTML must be sanitized before being rendered by React
  function santizeAndSetDangerousHtml(htmlString) {
    const sanitizedHtml = sanitizeHtml(htmlString, {
      allowedTags: ['b'],
    });
    return { __html: sanitizedHtml };
  }

  function labelOverride(items) {
    const selected = items.reduce((acc, option) => {
      return option.selected ? [...acc, option.label] : acc;
    }, []);

    const oneselected = selected.length === 1;
    const allSelected = !selected.length || selected.length === items.length;
    return oneselected ? selected[0] : allSelected ? 'Channel' : `${selected.length} Channels`;
  }

  function truncateLink(link) {
    return link.length > 120 ? `${link.slice(0, 120)}...` : link;
  }

  function setCopyLink(dateCreated, encodedAuctionUrl, auctionUrl) {
    // we want to only use shoplinks for links created during and after 2021-09-01
    return moment(dateCreated) >= moment('2021-09-01') ? encodedAuctionUrl : auctionUrl;
  }

  const columnItems = columns.map((column, index) => (
    <Column
      key={index}
      dataKey={column.dataKey}
      label={column.label}
      width={column.width}
      flexWidth={true}
      sortable={column.sortable}
      tooltip={column.tooltip}
      justifyContent={column.justifyContent}
      fontColor={column.fontColor}
      cellRender={column.cellRender}
      labelRender={column.labelRender}
    />
  ));

  const snackBar = snackbarTxt ? (
    <SnackBar message={snackbarTxt} setSnackbarTxt={setSnackbarTxt} timeout={1} />
  ) : null;

  return (
    <div className="manage-links-container">
      <div className="header">My Links</div>
      <MocksButton setShowMocks={setShowMocks} showMocks={showMocks} />
      <div className="content">
        <div className="table-area">
          <div className="table-options">
            <div className="left">
              <Link to={`/v2/publisher/${session.orgId}/links/create-links/select-story`}>
                <button className="create-btn">Create Links</button>
              </Link>
              <Search
                debounce={true}
                placeholder="Search..."
                onSearch={query => setSearchQuery(query)}
              />
            </div>
            <div className="right">
              <ConfirmDropdown
                items={channels}
                updateItems={setChannels}
                itemType="channels"
                labelOverride={labelOverride}
                confirmLabel="Done"
                defaultSelectNone={true}
                defaultHideDeselectAll={true}
              />
              <BasicDropdown
                className="user-type-dropdown"
                items={sourceOptions}
                selectItem={idx => setsource(sourceOptions[idx])}
                dropdownLabel={source.label}
              />
            </div>
          </div>
          <Table
            data={links}
            rowIdField="bam_link_id"
            onSort={({ field, order }) => {
              setSortField(field);
              setSortDirection(order);
            }}
            sortField={sortField}
            sortOrder={sortDirection}
          >
            {columnItems}
          </Table>
        </div>
        <div className="footer">
          {snackBar}
          <div className="pagination">
            <Pagination
              showPagination={true}
              currentPage={page}
              totalItems={totalItems}
              perPage={perPage}
              eventPrefix="Manage Links"
              updatePage={setPage}
              updatePerPage={setPerPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default withLDConsumer()(ManageLinks);
